import { AgTechEnumDisplayValues } from '@agtech/core/data/common/AgTechDataEntities';
import { AgTechButton, AgTechClickEventHandler, useClickable } from '../Buttons/AgTechButtons';
import { isNumber } from 'lodash';
import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { FormContext } from '@agtech/core/forms/AgTechFormContext';
import { AgTechFormTextControl } from './Controls/AgTechFormTextControl';
import { AgTechFormControlUpdate } from '@agtech/core/forms/AgTechFormControls';

export type AgTechFormDropdownCreationConfig<TItem> = {
    creationOptionText: string,
    onCreated: (e: AgTechClickEventHandler) => Promise<TItem>
}

export type AgTechFormDropdownProps<TEntity, TItem> = {
    entityType: TEntity,
    items: TItem[],
    text: (item: TItem) => string,
    value: (item: TItem) => string,
    caption?: string,
    selectedValue: string | undefined,
    onChanged: AgTechFormControlUpdate<TEntity, TItem>,
    item?: (item: TItem) => ReactNode,
    width?: number,
    readonlyIf?: (val: any) => boolean,
    isRequired?: boolean,
    showAsRequired?: boolean,
    creationConfig?: AgTechFormDropdownCreationConfig<TItem>
}

declare type AgTechDropdownItemMetadata<TItem> = {
    text: string,
    value: string,
    item: TItem,
    index: number
}

declare type AgTechDropdownState<TItem> = {
    isDropdownOpen: boolean,
    searchFilter: string,
    selectedText: string,
    activeIndex: number,
    sourceItems: TItem[],
    dropdownItems: AgTechDropdownItemMetadata<TItem>[],
    shownDropdownItems: AgTechDropdownItemMetadata<TItem>[],
    selectedDropdownItem: AgTechDropdownItemMetadata<TItem> | null,
    maxHeight: number,
    openUpward: boolean
}

const DefaultDropdownMaxHeight = 320;
const DropdownItemHeight = 34;
const DropdownCreationItemHeight = 40;

const initializeDropdownItems = <TEntity, TItem>(props: AgTechFormDropdownProps<TEntity, TItem>): AgTechDropdownItemMetadata<TItem>[] => {
    return props.items.map((item, index) => {
        let itemText = props.text(item);
        let itemValue = props.value(item);

        let itemMetadata: AgTechDropdownItemMetadata<TItem> = {
            text: itemText,
            value: itemValue,
            item: item,
            index: index
        };

        return itemMetadata;
    });
}

let calculateDropdownHeight = (listBox: React.MutableRefObject<HTMLDivElement>) => {
    let dropdownMaxHeight = DefaultDropdownMaxHeight + 1;

    if (listBox.current) {
        const dropdownMenu = listBox.current;

        let sumHeightRenderedItems = 0;
        let dropdownItems = dropdownMenu.querySelectorAll('.item');

        dropdownItems.forEach(item => {
            sumHeightRenderedItems += Math.ceil(item.clientHeight);
        });

        sumHeightRenderedItems += 8;

        if (sumHeightRenderedItems < dropdownMaxHeight) {
            dropdownMaxHeight = sumHeightRenderedItems;
        }
    }

    return dropdownMaxHeight - 2;
}

const AgTechFormSelectionControl = <TEntity, TItem>(props: AgTechFormDropdownProps<TEntity, TItem>) => {
    let formContext = useContext(FormContext);
    let formControlWidth: string = `${props.width ?? 50}%`;

    let [dropdownState, updateDropdownState] = useState<AgTechDropdownState<TItem>>({
        isDropdownOpen: false,
        searchFilter: '',
        selectedText: '',
        activeIndex: 0,
        sourceItems: props.items,
        dropdownItems: [],
        shownDropdownItems: [],
        selectedDropdownItem: null,
        maxHeight: DefaultDropdownMaxHeight,
        openUpward: false
    });

    let body = useRef(document.getElementById("root"));
    let listBox = useRef(document.createElement("div"));
    let creationItem = useRef<HTMLDivElement>(null);
    let creationItemClickable = useClickable(creationItem);

    useEffect(() => {
        let dropdownItems = initializeDropdownItems(props);
        let dropdownItemsWithSelectedValue = dropdownItems.filter(item => item.value === props.selectedValue);
        let selectedDropdownItem = dropdownItemsWithSelectedValue ? dropdownItemsWithSelectedValue[0] : null;

        let openUpward = false;
        let dropdownHeight = calculateDropdownHeight(listBox);

        if (body.current) {
            if (listBox.current.offsetTop + dropdownHeight > body.current.offsetHeight) {
                openUpward = true;
            }
        }

        updateDropdownState({
            ...dropdownState,
            dropdownItems: [...dropdownItems],
            shownDropdownItems: [...dropdownItems],
            selectedDropdownItem: selectedDropdownItem,
            selectedText: selectedDropdownItem?.text ?? '',
            activeIndex: selectedDropdownItem?.index ?? 0,
            maxHeight: dropdownHeight,
            openUpward: openUpward
        });
    }, []);

    useEffect(() => {
        if (dropdownState.dropdownItems.length > 0) {
            let dropdownMaxHeight = calculateDropdownHeight(listBox);

            updateDropdownState({
                ...dropdownState,
                maxHeight: dropdownMaxHeight
            });
        }
    }, [dropdownState.shownDropdownItems.length]);

    let handleSearchUpdate = (e: React.ChangeEvent<HTMLInputElement>) => {
        let updatedSearchFilter = e.currentTarget.value;

        updateDropdownState({
            ...dropdownState,
            searchFilter: updatedSearchFilter,
            shownDropdownItems: dropdownState.dropdownItems.filter(item => item.text.startsWith(updatedSearchFilter)),
            isDropdownOpen: true
        });
    };

    let handleKeyPressed = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            moveActiveItemUp();
        }
        else if (e.key === 'ArrowDown') {
            e.preventDefault();
            moveActiveItemDown();
        }
        else if (e.key === 'Enter') {
            e.preventDefault();

            const dropdownItemListBox: any = listBox.current;
            const shownItems = dropdownItemListBox.querySelectorAll('[role="option"]');

            let indexOfItemToSelect = shownItems.length === 1 ? parseInt(shownItems[0].dataset.index) : dropdownState.activeIndex;
            
            let selectedItem = getSelectedItem(indexOfItemToSelect);

            if (selectedItem) {
                handleItemSelected(selectedItem, {
                    ...dropdownState,
                    shownDropdownItems: [...dropdownState.dropdownItems],
                    searchFilter: '',
                    isDropdownOpen: false
                });
            }
        }
    }

    let moveActiveItemUp = () => {
        const dropdownItemListBox: any = listBox.current;

        if (dropdownItemListBox && dropdownItemListBox.clientHeight > 0) {
            const activeItem = dropdownItemListBox.querySelector('.item.active');

            if (activeItem) {
                const previousItem = activeItem.previousSibling;
                
                if (previousItem) {
                    const previousItemIndex: number = parseInt(previousItem.dataset.index);

                    let selectedItem = dropdownState.shownDropdownItems.filter(item => item.index === previousItemIndex)[0];
                    handleItemSelected(selectedItem);   
                }
            }
            else {
                const shownItems = dropdownItemListBox.querySelectorAll('.item');
                const lastShownItem = shownItems[shownItems.length - 1];

                if (lastShownItem) {
                    const lastShownItemIndex: number = parseInt(lastShownItem.dataset.index);

                    let selectedItem = dropdownState.shownDropdownItems.filter(item => item.index === lastShownItemIndex)[0];
                    handleItemSelected(selectedItem);   
                }
            }
        }
    }

    let moveActiveItemDown = () => {
        const dropdownItemListBox: any = listBox.current;

        if (dropdownItemListBox && dropdownItemListBox.clientHeight > 0) {
            const activeItem = dropdownItemListBox.querySelector('.item.active');

            if (activeItem) {
                const nextItem = activeItem.nextSibling;
                
                if (nextItem) {
                    const nextItemIndex: number = parseInt(nextItem.dataset.index);

                    let selectedItem = dropdownState.shownDropdownItems.filter(item => item.index === nextItemIndex)[0];
                    handleItemSelected(selectedItem);                }
            }
            else {
                const shownItems = dropdownItemListBox.querySelectorAll('.item');

                if (shownItems) {
                    const firstShownItem = shownItems[0];

                    if (firstShownItem) {
                        const firstShownItemIndex: number = parseInt(firstShownItem.dataset.index);

                        let selectedItem = dropdownState.shownDropdownItems.filter(item => item.index === firstShownItemIndex)[0];
                        handleItemSelected(selectedItem);
                    }
                }
            }
        }
    }

    let getSelectedItem = (activeIndex: number) => {
        return dropdownState.dropdownItems.filter(item => item.index === activeIndex)[0];
    }

    let handleItemClicked = (e: React.MouseEvent, item: AgTechDropdownItemMetadata<TItem>) => {       
        // If the mouse down event was a left click, proceed with selecting the clicked item
        if (e.button === 0) {
            handleItemSelected(item);
        }
    }

    let handleItemSelected = (
        item: AgTechDropdownItemMetadata<TItem>,
        updatedState?: AgTechDropdownState<TItem>) => {
        formContext.handleFieldChanged(item.item, props.onChanged);

        updateDropdownState({
            ...(updatedState ?? dropdownState),
            selectedDropdownItem: item,
            selectedText: item.text,
            activeIndex: item.index
        });
    }

    let handleItemCreated = (item: AgTechDropdownItemMetadata<TItem>) => {
        let updatedSourceItems = [...dropdownState.sourceItems, item.item];
        let updatedDropdownItems = [...dropdownState.dropdownItems, item];

        handleItemSelected(item, {
            ...dropdownState,
            sourceItems: updatedSourceItems,
            dropdownItems: updatedDropdownItems,
            shownDropdownItems: updatedDropdownItems.filter(item => item.text.startsWith(dropdownState.searchFilter)),
            isDropdownOpen: false
        });
    }

    let handleItemCleared = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        formContext.handleFieldChanged(undefined as unknown as TItem, props.onChanged);

        updateDropdownState({
            ...dropdownState,
            selectedDropdownItem: null,
            selectedText: '',
            activeIndex: 0
        });
    }

    let scrollOnItemActive = () => {
        const dropdownItemListBox: any = listBox.current;

        if (dropdownItemListBox && dropdownItemListBox.clientHeight > 0) {
            const activeItem = dropdownItemListBox.querySelector('.item.active');

            if (activeItem) {
                const isOutOfUpperView = activeItem.offsetTop < dropdownItemListBox.scrollTop
                const isOutOfLowerView = activeItem.offsetTop + activeItem.clientHeight > dropdownItemListBox.scrollTop + dropdownItemListBox.clientHeight
            
                if (isOutOfUpperView) {
                    dropdownItemListBox.scrollTop = activeItem.offsetTop
                } else if (isOutOfLowerView) {
                  // eslint-disable-next-line no-mixed-operators
                  dropdownItemListBox.scrollTop = activeItem.offsetTop + activeItem.clientHeight - dropdownItemListBox.clientHeight
                }
            }
        }
    }

    let closeDropdown = () => {
        updateDropdownState({
            ...dropdownState,
            searchFilter: '',
            isDropdownOpen: false
        });
    }

    let openDropdown = () => {
        let dropdownHeight = dropdownState.maxHeight;

        if (dropdownHeight < 10) {
            dropdownHeight = calculateDropdownHeight(listBox);
        }

        if (props.isRequired && !props.selectedValue && props.items.length > 0 && !dropdownState.selectedDropdownItem) {
            handleItemSelected(getSelectedItem(0), {
                ...dropdownState,
                maxHeight: dropdownHeight,
                isDropdownOpen: true
            });
        }
        else {
            updateDropdownState({
                ...dropdownState,
                maxHeight: dropdownHeight,
                isDropdownOpen: true });
        }
    }

    useEffect(() => {
        scrollOnItemActive();
    }, [dropdownState.activeIndex]);

    let handleCreationButtonClicked = async (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();

        if (props.creationConfig) {
            let createdItem = await props.creationConfig.onCreated({
                ...creationItemClickable,
                clickEvent: e
            });

            let createdItemMetadata: AgTechDropdownItemMetadata<TItem> = {
                text: props.text(createdItem),
                value: props.value(createdItem),
                item: {...createdItem},
                index: dropdownState.sourceItems.length
            }

            handleItemCreated(createdItemMetadata);
        }
    }

    let shouldShowAsRequired = props.isRequired && (props.showAsRequired ?? true);

    return (
        <div className="agtech-form-control" style={{maxWidth: formControlWidth}}>
            { props.caption ? <h1 className="agtech-form-control-caption">{props.caption + (shouldShowAsRequired ? ' *' : '')}</h1> : null }

            <div
                role="combobox"
                className="agtech-dropdown"
                onFocus={openDropdown}
                onClick={openDropdown}
                onKeyDown={handleKeyPressed}
                onBlur={closeDropdown}
            >
                <input
                    aria-autocomplete="list"
                    autoComplete="off"
                    className="search agtech-dropdown-search"
                    tabIndex={0}
                    type="text"
                    value={dropdownState.searchFilter}
                    onChange={handleSearchUpdate}
                />

                <div
                    aria-atomic="true"
                    aria-live="polite"
                    role="alert"
                    className={`divider text agtech-dropdown-text ${dropdownState.searchFilter ? 'filtered' : ''}
                                ${props.isRequired ? 'required' : ''} ${dropdownState.isDropdownOpen ? 'active' : ''}`}
                >
                    {dropdownState.selectedText}
                </div>

                {!props.isRequired && dropdownState.selectedDropdownItem ? 
                    <i className="fas fa-times agtech-dropdown-icon" onClick={handleItemCleared} /> : 
                    <i className="fas fa-caret-down agtech-dropdown-icon" /> }

                <div
                    role="listbox"
                    className={`agtech-dropdown-item-list
                        ${dropdownState.isDropdownOpen ? 'visible' : ''}
                        ${dropdownState.maxHeight === DefaultDropdownMaxHeight ? 'scrollable' : ''}
                        ${dropdownState.openUpward ? 'upward' : ''}
                    `}
                    style={{height: dropdownState.isDropdownOpen ? dropdownState.maxHeight : 0}}
                    ref={listBox}
                >
                    { 
                        props.creationConfig && dropdownState.dropdownItems && dropdownState.dropdownItems.length > 0 ? 
                            <div key="AddItem" className={`item agtech-dropdown-item agtech-dropdown-add-item`} onMouseDown={handleCreationButtonClicked}>
                                <i className='agtech-icon fas fa-plus' />
                                { props.creationConfig.creationOptionText}
                            </div>
                        : null 
                    }

                    {
                        props.creationConfig && (!dropdownState.dropdownItems || dropdownState.dropdownItems.length === 0) ?
                            <div key="AddItem" className={`item wide centered container agtech-dropdown-item agtech-dropdown-empty-add-item`}>
                                <AgTechButton text={props.creationConfig.creationOptionText} icon='fas fa-plus' action={e => handleCreationButtonClicked(e.clickEvent)} />
                            </div>
                            : null
                    }
                    
                    {
                        dropdownState.shownDropdownItems.map(item => {
                            let activeIndex = dropdownState.activeIndex;
                            let isActiveItem: boolean = item.index === dropdownState.activeIndex;
                            let isSelectedItem: boolean = (dropdownState.selectedDropdownItem && (item.index === dropdownState.selectedDropdownItem.index)) ?? false;

                            let itemActiveClass = isActiveItem ? 'active' : '';
                            let itemSelectedClass = isSelectedItem ? 'selected' : '';

                            return (
                                <div
                                    role="option" key={item.value} aria-checked={item.index === activeIndex} aria-selected={item.index === activeIndex}
                                    className={`item agtech-dropdown-item ${itemActiveClass} ${itemSelectedClass}`}
                                    data-index={item.index}
                                    onMouseDown={e => handleItemClicked(e, item)}
                                >
                                    { props.item ? props.item(item.item) : <span className="text">{item.text}</span>}
                                </div>
                            );
                        })
                    }
                </div>
            </div>
        </div>
    );
}

export const AgTechFormDropdownControl = <TEntity, TItem>(props: AgTechFormDropdownProps<TEntity, TItem>) => {
    let [isFirstRender, setIsFirstRender] = useState<boolean>(true);
    let [isReadonly, setIsReadonly] = useState<boolean>(false);

    useEffect(() => {
        let shouldBeReadonly: boolean = (props && props.readonlyIf && props.readonlyIf(props.selectedValue)) ?? false;

        setIsReadonly(shouldBeReadonly);
        setIsFirstRender(false);
    }, []);

    if (props.readonlyIf && props.readonlyIf(props.selectedValue) && (isFirstRender || isReadonly)) {
        let selectedItems: TItem[] = props.items.filter(item => props.value(item) === props.selectedValue);

        if (selectedItems && selectedItems.length === 1) {
            let selectedItem: TItem = selectedItems[0];

            return (
                <AgTechFormTextControl
                    entityType={{}}
                    value={i => props.text(selectedItem)}
                    caption={props.caption ?? ''}
                    readonly={true}
                    width={props.width}
                />
            )
        }
    }

    isFirstRender = false;

    return (
        <AgTechFormSelectionControl {...props} />
    )
};

export type AgTechCoreFormEnumDropdownProps<TEnum> = {
    item?: (enumValue: TEnum) => ReactNode,
    width?: number,
    readonlyIf?: (val: any) => boolean,
    isRequired?: boolean
}

export type AgTechFormEnumDropdownProps<TEntity, TEnum> = AgTechCoreFormEnumDropdownProps<TEnum> & {
    entityType: TEntity,
    caption: string,
    selectedValue: number | undefined,
    onChanged: AgTechFormControlUpdate<TEntity, number>,
    enum: any,
    displayValues?: AgTechEnumDisplayValues,
    requireEnumSelected?: boolean
}

export const AgTechFormEnumDropdownControl = <TEntity, TEnum extends any>(props: AgTechFormEnumDropdownProps<TEntity, TEnum>) => {
    type EnumDropdownItem = {
        text: string,
        value: number
    };

    let dropdownEnumItems: EnumDropdownItem[] = [];

    let enumValues = Object.values(props.enum).filter(val => isNumber(val)) as number[];

    for (const enumValue of enumValues) {
        dropdownEnumItems.push({
            text: props.displayValues ? props.displayValues[enumValue] : props.enum[enumValue],
            value: enumValue
        });
    }

    const onEnumValueSelected: AgTechFormControlUpdate<TEntity, EnumDropdownItem> = async (entity, selectedEnumItem) => {
        return await props.onChanged(entity, selectedEnumItem.value);
    };

    return (
        <AgTechFormSelectionControl
            entityType={props.entityType}
            caption={props.caption}
            items={dropdownEnumItems}
            selectedValue={props.selectedValue?.toString()}
            text={(p) => p.text}
            value={(p) => p.value.toString()}
            onChanged={onEnumValueSelected}
            isRequired={props.isRequired || props.requireEnumSelected}
            showAsRequired={!props.requireEnumSelected}
            width={props.width} />
    )
};